import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import api from '../../../services/api';

/**
 * Componente para gerenciamento de API Keys do PrinterBridge
 * Permite criar, listar e revogar API Keys para conexões WebSocket
 */
const ApiKeyManagement = ({ customTheme, onAlert }) => {
  // Estados do componente
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', description: '' });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [creating, setCreating] = useState(false);

  // Carrega as API Keys existentes
  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/printer-bridge/api-keys');
      
      if (response.data && response.data.success) {
        setApiKeys(response.data.apiKeys || []);
      } else {
        // Se não há success=true, mas há dados, pode ser que não tenha API Keys
        if (response.data && response.data.message) {
          console.log('Resposta do servidor:', response.data.message);
          setApiKeys([]);
        } else {
          throw new Error(response.data?.message || 'Erro ao carregar API Keys');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar API Keys:', error);
      onAlert?.({
        message: 'Erro ao carregar lista de API Keys: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
      setApiKeys([]); // Define lista vazia em caso de erro
    } finally {
      setLoading(false);
    }
  };

  // Carrega as API Keys na inicialização
  useEffect(() => {
    loadApiKeys();
  }, []);

  // Abre o diálogo para criar nova API Key
  const handleOpenCreateDialog = () => {
    setNewKeyData({ name: '', description: '' });
    setGeneratedKey(null);
    setDialogOpen(true);
  };

  // Fecha o diálogo
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewKeyData({ name: '', description: '' });
    setGeneratedKey(null);
    setShowGeneratedKey(false);
  };

  // Cria uma nova API Key
  const handleCreateApiKey = async () => {
    if (!newKeyData.name.trim()) {
      onAlert?.({
        message: 'Nome da API Key é obrigatório',
        severity: 'warning'
      });
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/printer-bridge/api-keys', {
        name: newKeyData.name.trim(),
        description: newKeyData.description.trim() || undefined,
      });

      if (response.data && response.data.success) {
        setGeneratedKey({
          apiKey: response.data.apiKey,
          keyId: response.data.keyId,
          name: response.data.name,
        });
        
        onAlert?.({
          message: 'API Key criada com sucesso! Guarde-a em local seguro.',
          severity: 'success'
        });

        // Recarrega a lista
        await loadApiKeys();
      } else {
        throw new Error(response.data?.message || 'Erro ao criar API Key');
      }
    } catch (error) {
      console.error('Erro ao criar API Key:', error);
      onAlert?.({
        message: 'Erro ao criar API Key: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  // Revoga uma API Key
  const handleRevokeApiKey = async (keyId, keyName) => {
    if (!window.confirm(`Tem certeza que deseja revogar a API Key "${keyName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const response = await api.post(`/printer-bridge/api-keys/${keyId}/revoke`);

      if (response.data && response.data.success) {
        onAlert?.({
          message: `API Key "${keyName}" revogada com sucesso`,
          severity: 'success'
        });

        // Recarrega a lista
        await loadApiKeys();
      } else {
        throw new Error(response.data?.message || 'Erro ao revogar API Key');
      }
    } catch (error) {
      console.error('Erro ao revogar API Key:', error);
      onAlert?.({
        message: 'Erro ao revogar API Key: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    }
  };

  // Copia texto para a área de transferência
  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      onAlert?.({
        message: `${label} copiado para a área de transferência`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao copiar:', error);
      onAlert?.({
        message: 'Erro ao copiar para a área de transferência',
        severity: 'error'
      });
    }
  };

  // Formata data para exibição
  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Mascara a API Key para exibição
  const maskApiKey = (key) => {
    if (!key || key.length < 8) return key;
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`;
  };

  return (
    <Box>
      {/* Cabeçalho da seção */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <KeyIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Gerenciamento de API Keys
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ 
            bgcolor: customTheme.palette.primary.main,
            '&:hover': {
              bgcolor: customTheme.palette.primary.dark,
            }
          }}
        >
          Nova API Key
        </Button>
      </Box>

      {/* Informações sobre API Keys */}
      <Alert 
        severity="info" 
        sx={{ mb: 3 }}
        icon={<KeyIcon />}
      >
        <Typography variant="body2">
          <strong>API Keys</strong> são necessárias para conectar o Printer Bridge ao sistema. 
          Cada chave é única e deve ser mantida em segurança. Use nomes descritivos para 
          identificar facilmente cada instalação.
        </Typography>
      </Alert>

      {/* Lista de API Keys */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {apiKeys.length === 0 ? (
            <Card sx={{ 
              border: '2px dashed',
              borderColor: alpha(customTheme.palette.primary.main, 0.3),
              bgcolor: alpha(customTheme.palette.primary.main, 0.05),
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <KeyIcon sx={{ 
                  fontSize: 48, 
                  color: alpha(customTheme.palette.primary.main, 0.5),
                  mb: 2 
                }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma API Key encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Crie sua primeira API Key para começar a usar o Printer Bridge
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateDialog}
                >
                  Criar primeira API Key
                </Button>
              </CardContent>
            </Card>
          ) : (
            apiKeys.map((key) => (
              <Card key={key.id} sx={{ 
                border: '1px solid',
                borderColor: alpha(customTheme.palette.divider, 0.2),
                '&:hover': {
                  borderColor: customTheme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${alpha(customTheme.palette.primary.main, 0.2)}`,
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {key.name}
                        </Typography>
                        <Chip
                          label={key.isActive ? 'Ativa' : 'Inativa'}
                          color={key.isActive ? 'success' : 'error'}
                          size="small"
                          sx={{ ml: 2 }}
                          icon={key.isActive ? <CheckCircleIcon /> : <ErrorIcon />}
                        />
                      </Box>
                      
                      {key.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {key.description}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Criada:</strong> {formatDate(key.createdAt)}
                        </Typography>
                        {key.lastUsedAt && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Último uso:</strong> {formatDate(key.lastUsedAt)}
                          </Typography>
                        )}
                        {key.expiresAt && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Expira:</strong> {formatDate(key.expiresAt)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary">
                        <strong>ID:</strong> {key.id}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copiar ID da chave">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(key.id, 'ID da API Key')}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {key.isActive && (
                        <Tooltip title="Revogar API Key">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeApiKey(key.id, key.name)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      {/* Diálogo para criar nova API Key */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <KeyIcon sx={{ mr: 1 }} />
            {generatedKey ? 'API Key Criada com Sucesso!' : 'Criar Nova API Key'}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {generatedKey ? (
            // Exibe a API Key gerada
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>API Key criada com sucesso!</strong> Esta é a única vez que ela será exibida. 
                  Copie e guarde em local seguro.
                </Typography>
              </Alert>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: alpha(customTheme.palette.primary.main, 0.05),
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(customTheme.palette.primary.main, 0.2),
                mb: 2
              }}>
                <Typography variant="subtitle2" gutterBottom>
                  Nome: {generatedKey.name}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  ID: {generatedKey.keyId}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">
                    API Key: 
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      flex: 1,
                      bgcolor: 'background.paper',
                      p: 1,
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {showGeneratedKey ? generatedKey.apiKey : maskApiKey(generatedKey.apiKey)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                  >
                    {showGeneratedKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(generatedKey.apiKey, 'API Key')}
                  >
                    <CopyIcon />
                  </IconButton>
                </Box>
              </Box>
              
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Importante:</strong> Guarde esta API Key em local seguro. 
                  Ela não poderá ser recuperada posteriormente.
                </Typography>
              </Alert>
            </Box>
          ) : (
            // Formulário para criar nova API Key
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Nome da API Key"
                placeholder="Ex: Recepção - PC01"
                value={newKeyData.name}
                onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                fullWidth
                required
                helperText="Use um nome descritivo para identificar esta instalação"
              />
              
              <TextField
                label="Descrição (opcional)"
                placeholder="Ex: Computador da recepção, usado para impressão de senhas"
                value={newKeyData.description}
                onChange={(e) => setNewKeyData({ ...newKeyData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                helperText="Descrição adicional para facilitar a identificação"
              />
              
              <Alert severity="info">
                <Typography variant="body2">
                  A API Key será gerada automaticamente com prefixo "pb_" e terá validade indefinida. 
                  Você poderá revogá-la a qualquer momento.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {generatedKey ? 'Fechar' : 'Cancelar'}
          </Button>
          
          {!generatedKey && (
            <Button
              variant="contained"
              onClick={handleCreateApiKey}
              disabled={creating || !newKeyData.name.trim()}
              startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {creating ? 'Criando...' : 'Criar API Key'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeyManagement; 